/**
 * @module WordBasedTruncation
 * @date 03/08/2017
 * @copyright 2017 Empower Retirement
 * @description
 *
 *      Truncates text based on the number of words and wraps it with more/less links to see more/less.
 *      When showing the truncated view we use ellipses.
 *
 */

import * as _ from "lodash";
import truncate from "html-truncate";
import StringUtil from "../../util/StringUtil";

/**
 * The constructor's parameters are "injected" via AJS's IoC.
 *
 * @constructor
 */
var WordBasedTruncation = function () {
    /**
     * The HTML show more/less wrapper for the component.
     * @type {String}
     */
    var MORE_LESS_HTML =
        "" +
        "<span ng-show='!open'>{0}&nbsp;</span>" +
        "<span role='link' tabindex='0' class='btn-link ngTruncateToggleText' ng-click='toggleShow()' ng-show='!open'>{1}</span>" +
        "<span role='link' tabindex='0' ng-show='open'>{2}&nbsp;<span class='btn-link ngTruncateToggleText' ng-click='toggleShow()'>{3}</span></span>";

    //-----------------------------------------------------------------------
    // PROTECTED METHODS
    //-----------------------------------------------------------------------

    /**
     * Determines if truncation should be applied based on the length of the text and
     * the threshold number.
     *
     * @param {String} text - The text being inspected for possible truncation.
     * @param {Number} threshold - The number of words to allow before truncation.
     * @returns {Boolean}
     */
    var truncationApplies = function (text, threshold) {
        text = _.trimEnd(text);
        return text.split(" ").length > threshold;
    };

    /**
     * Returns a string of words with less than or equal to the threshold.
     *
     * @param {String} text - The text being inspected for possible truncation.
     * @param {Number} threshold - The number of words to allow before truncation.
     * @returns {String}
     */
    var getWordsToThreshold = function (text, threshold) {
        var splitText = text.split(" ");
        return _.trimEnd(splitText.slice(0, threshold).join(" "));
    };

    /**
     * Removes all HTML tags from the text.
     *
     * @param {String} text - The text being inspected for HTML.
     * @returns {String} The text input sans HTML tags.
     */
    var makeHtmlSafe = function (text) {
        return truncate(text, text.length);
    };

    /**
     * Truncates text based on the number of words and wraps it with more/less links to see more/less.
     *
     * @param {String} text - The text to apply truncation to.
     * @param {Number} threshold - The number of words to allow before truncating.
     * @param {String} customMoreLabel - The label for showing more link.
     * @param {String} customLessLabel - The label for showing less link.
     * @param {Boolean} useToggling - Indicates if the links should show.
     */
    var applyTruncation = function (
        text,
        threshold,
        customMoreLabel,
        customLessLabel,
        useToggling
    ) {
        var moreContent = makeHtmlSafe(getWordsToThreshold(text, threshold));
        var lessContent = text;
        var moreLabel = customMoreLabel ? customMoreLabel : "More";
        var lessLabel = customLessLabel ? customLessLabel : "Less";
        var result = "";

        if (useToggling) {
            var tokens = [moreContent, moreLabel, lessContent, lessLabel];
            result = StringUtil.supplant(MORE_LESS_HTML, tokens);
        } else {
            result = getWordsToThreshold(text, threshold) + "...";
        }

        return result;
    };

    //-----------------------------------------------------------------------
    // EXPOSED API
    //-----------------------------------------------------------------------

    return {
        truncationApplies: truncationApplies,
        applyTruncation: applyTruncation,
        MORE_LESS_HTML: MORE_LESS_HTML
    };
};

WordBasedTruncation.$inject = [];
export default WordBasedTruncation;
